/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { PageInfo } from 'src/core/utils/pagination/pagination';
import {
    AddExampleSwagger,
    AddExampleSwaggerPayload,
    BatchAddExampleSwagger,
    exampleSwaggerToUtteranceStore,
    PredictionSwagger,
    predictionSwaggerToPredictionStore,
    UtteranceSwagger
} from 'src/luis/projects/utterances/services/mappers';
import { PredictionStore } from 'src/luis/projects/utterances/stores/predictionStore';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { LuisId } from 'src/luis/types/Resource';

type GetLabelsParams = {
    modelId: string;
    pageInfo: PageInfo;
};

/**
 * Represents a service for manipulation of utterances.
 */
export const getUtteranceService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({
        apiVersion: 'v3.0-preview'
    });
    const { webApiUrl } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * A base Url builder for the rest of the apis.
     * @param isWebAPI
     */
    const getBaseUrlBuilder = (isWebAPI: boolean = false) =>
        urlBuilder()
            .baseUrl(isWebAPI ? webApiUrl : apiUrl)
            .app(context.appId)
            .version(context.versionId);

    /**
     * Gets labels of a model.
     */
    const getLabels = async ({ modelId, pageInfo }: GetLabelsParams): Promise<readonly UtteranceSwagger[]> => {
        const url = getBaseUrlBuilder(true)
            .resource('models', modelId)
            .resource('reviewLabels')
            .queryParam('skip', `${pageInfo.skip}`)
            .queryParam('take', `${pageInfo.take}`)
            .build();

        const { data: utteranceItems } = await httpService.get<readonly UtteranceSwagger[]>(url, apiOptions);

        return utteranceItems;
    };

    /**
     * Gets predictions of a model.
     */
    const getPredictions = async ({ modelId, pageInfo }: GetLabelsParams): Promise<readonly PredictionSwagger[]> => {
        const url = getBaseUrlBuilder(true)
            .resource('models', modelId)
            .resource('reviewPredictions')
            .queryParam('skip', `${pageInfo.skip}`)
            .queryParam('take', `${pageInfo.take}`)
            .build();

        const { data: predictionItems } = await httpService.get<readonly PredictionSwagger[]>(url, apiOptions);

        return predictionItems;
    };

    /**
     * Adds an utterance to the specified model.
     */
    const addUtterance = async ({
        payload,
        intentId,
        enableSendingLabelsInNestedFormat
    }: {
        payload: AddExampleSwaggerPayload;
        intentId: LuisId;
        enableSendingLabelsInNestedFormat: boolean;
    }) => {
        const url = getBaseUrlBuilder()
            .resource('example')
            .queryParam('enableNestedChildren', `${enableSendingLabelsInNestedFormat}`)
            .build();

        const { data: addExampleSwagger } = await httpService.post<AddExampleSwagger>(url, payload, apiOptions);

        return exampleSwaggerToUtteranceStore(addExampleSwagger, <string>intentId, []);
    };

    /**
     * Adds an array of utterances to the specified model.
     */
    const addUtterances = async ({
        payload,
        enableSendingLabelsInNestedFormat
    }: {
        payload: AddExampleSwaggerPayload[];
        enableSendingLabelsInNestedFormat: boolean;
    }) => {
        const url = getBaseUrlBuilder()
            .resource('examples')
            .queryParam('enableNestedChildren', `${enableSendingLabelsInNestedFormat}`)
            .build();

        const { data } = await httpService.post<BatchAddExampleSwagger>(url, payload, apiOptions);

        return data;
    };

    /**
     * Deletes a label using its id.
     */
    const deleteUtterance = async ({ id }: Pick<UtteranceStore, 'id'>): Promise<void> => {
        const url = getBaseUrlBuilder()
            .resource('examples', `${id}`)
            .build();

        await httpService.delete(url, apiOptions);
    };

    /**
     * Batch Predicts utterances.
     */
    const batchPredict = async ({ textArr }: { textArr: string[] }): Promise<PredictionStore[]> => {
        if (textArr.length === 0) {
            return Promise.resolve([]);
        }

        const url = getBaseUrlBuilder(true)
            .resource('predict')
            .build();

        return await httpService
            .post<readonly PredictionSwagger[]>(url, textArr, apiOptions)
            .then(predictions => predictions.data.map(predictionSwaggerToPredictionStore));
    };

    return {
        getLabels,
        getPredictions,
        deleteUtterance,
        addUtterances,
        addUtterance,
        batchPredict
    };
});
